package ai.jessie.web.documents

import kotlin.js.Date
import kotlin.js.json

// BR-106 and B2: the requirements shown before selection (step 2). The server decides; the
// `accept` list only narrows the browser's file picker.
internal const val MAX_DOCUMENT_BYTES = 26_214_400L
internal const val SUPPORTED_TYPES = "PDF, JPEG, PNG, Text (TXT), CSV, DOCX und XLSX"
internal const val ACCEPTED_FILES =
    ".pdf,.jpg,.jpeg,.png,.txt,.csv,.docx,.xlsx,application/pdf,image/jpeg,image/png," +
        "text/plain,text/csv," +
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

// Display only: the server's canonical media type (B2) as the short type name.
internal fun documentTypeLabel(mediaType: String) =
    when (mediaType) {
        "application/pdf" -> "PDF"
        "image/jpeg" -> "JPEG"
        "image/png" -> "PNG"
        "text/plain" -> "Text"
        "text/csv" -> "CSV"
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" -> "DOCX"
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" -> "XLSX"
        else -> "Unbekannter Typ"
    }

// The exact byte count with Swiss grouping, plus a rounded binary unit above 1 KiB.
internal fun sizeLabel(bytes: Long): String {
    val exact = "${grouped(bytes)} Bytes"
    val rounded =
        when {
            bytes >= MIB -> "${oneDecimal(bytes.toDouble() / MIB)} MiB"
            bytes >= KIB -> "${oneDecimal(bytes.toDouble() / KIB)} KiB"
            else -> return exact
        }
    return "$rounded ($exact)"
}

// The authoritative server upload time in the browser's local time; unparsable values stay as sent.
internal fun uploadTimeLabel(instant: String): String {
    val date = Date(instant)
    if (date.getTime().isNaN()) return instant
    val options: dynamic =
        json(
            "day" to "2-digit",
            "month" to "2-digit",
            "year" to "numeric",
            "hour" to "2-digit",
            "minute" to "2-digit",
        )
    return date.asDynamic().toLocaleString("de-CH", options) as String
}

private fun grouped(value: Long): String =
    value.toString().reversed().chunked(GROUP).joinToString("’").reversed()

private fun oneDecimal(value: Double): String {
    val tenths = kotlin.math.round(value * TENTHS).toLong()
    return "${tenths / TENTHS}.${tenths % TENTHS}"
}

private const val KIB = 1024L
private const val MIB = 1024L * 1024L
private const val GROUP = 3
private const val TENTHS = 10
